import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_COLLAPSED_STATUSES, WorkflowStatus, WorkflowType } from './config';

// =======================================
// COLLAPSED COLUMNS
// =======================================

const storageKey = (type: WorkflowType) => `workflows.collapsed.${type}`;

/**
 * Stored by status name, not id: the ids differ between local, staging and
 * production, the names do not. Null when this viewer has never changed the
 * board (or storage is unavailable), meaning "use the defaults".
 */
const readStored = (type: WorkflowType): string[] | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(type));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    return null;
  }
};

const writeStored = (type: WorkflowType, names: string[]) => {
  try {
    window.localStorage.setItem(storageKey(type), JSON.stringify(names));
  } catch {
    // A private window or blocked storage: the choice lasts until reload.
  }
};

/**
 * Which columns of a board are collapsed to a slim rail. A per-viewer
 * convenience, remembered in this browser per board type; Complete and
 * Archived start collapsed.
 */
export const useCollapsedColumns = (type: WorkflowType, statuses: WorkflowStatus[]) => {
  const [names, setNames] = useState<string[]>(
    () => readStored(type) ?? DEFAULT_COLLAPSED_STATUSES,
  );

  // Only a choice the viewer made is written back; loading a board's stored
  // (or default) state must not save the defaults as if chosen.
  const viewerChanged = useRef(false);

  // Switching boards reads that board's own choice.
  useEffect(() => {
    viewerChanged.current = false;
    setNames(readStored(type) ?? DEFAULT_COLLAPSED_STATUSES);
  }, [type]);

  useEffect(() => {
    if (viewerChanged.current) writeStored(type, names);
  }, [type, names]);

  const collapsedIds = useMemo(
    () => new Set(statuses.filter(s => names.includes(s.status_name)).map(s => s.id)),
    [statuses, names],
  );

  // Reads the current names inside the updater, so the callbacks below keep
  // one identity while columns open and close -- the lanes are memoised, and
  // a new callback on every toggle re-rendered all of them.
  const update = useCallback((statusId: number, collapsed: boolean | 'toggle') => {
    const name = statuses.find(s => s.id === statusId)?.status_name;
    if (!name) return;
    viewerChanged.current = true;
    setNames(prev => {
      const has = prev.includes(name);
      const want = collapsed === 'toggle' ? !has : collapsed;
      if (has === want) return prev;
      return want ? [...prev, name] : prev.filter(n => n !== name);
    });
  }, [statuses]);

  const setCollapsed = useCallback(
    (statusId: number, collapsed: boolean) => update(statusId, collapsed),
    [update],
  );
  const toggleCollapsed = useCallback((statusId: number) => update(statusId, 'toggle'), [update]);

  return { collapsedIds, setCollapsed, toggleCollapsed };
};

// =======================================
// COLUMN SCROLL-SPY AND JUMPS
// =======================================

// Quiet time after the last scroll event before a programmatic jump is
// treated as finished (Safari has no scrollend event).
const JUMP_SETTLE_MS = 160;

/**
 * The column the board is scrolled to, for the column rail, and a jump to
 * any column. The active column is the first lane whose middle is still right
 * of the board's left padding edge. While a jump scrolls, the spy stands down
 * so the rail does not flicker through every column in between, and a jump to
 * a column that cannot reach the left edge (the last few) stays selected.
 */
export const useColumnScrollSpy = (
  boardRef: React.RefObject<HTMLDivElement | null>,
  statuses: WorkflowStatus[],
  ready: boolean,
) => {
  const [activeId, setActiveId] = useState<number | null>(null);
  const jumpingRef = useRef(false);
  const settleTimer = useRef<number | undefined>(undefined);

  const measure = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    const lanes = Array.from(board.querySelectorAll<HTMLElement>('[data-status-id]'));
    if (lanes.length === 0) return;
    const edge = board.scrollLeft + parseFloat(getComputedStyle(board).paddingLeft || '0');
    const lane = lanes.find(l => l.offsetLeft + l.offsetWidth / 2 >= edge) ?? lanes[lanes.length - 1];
    const id = Number(lane.dataset.statusId);
    setActiveId(prev => (prev === id ? prev : id));
  }, [boardRef]);

  useEffect(() => {
    const board = boardRef.current;
    if (!ready || !board) return;
    let frame = 0;
    const onScroll = () => {
      if (jumpingRef.current) {
        window.clearTimeout(settleTimer.current);
        settleTimer.current = window.setTimeout(() => {
          jumpingRef.current = false;
        }, JUMP_SETTLE_MS);
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    board.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer.current);
      board.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [boardRef, measure, ready, statuses]);

  /**
   * Scroll a column to the board's left edge. Waits two frames so a column
   * expanded in the same click has laid out at full width first.
   */
  const jumpTo = useCallback((statusId: number) => {
    setActiveId(statusId);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const board = boardRef.current;
      const lane = board?.querySelector<HTMLElement>(`[data-status-id="${statusId}"]`);
      if (!board || !lane) return;
      const padding = parseFloat(getComputedStyle(board).paddingLeft || '0');
      const left = Math.max(0, Math.min(lane.offsetLeft - padding, board.scrollWidth - board.clientWidth));
      if (Math.abs(board.scrollLeft - left) < 1) return;
      jumpingRef.current = true;
      window.clearTimeout(settleTimer.current);
      // Released even if no scroll event arrives (already there, or blocked).
      settleTimer.current = window.setTimeout(() => {
        jumpingRef.current = false;
      }, 1000);
      board.scrollTo({ left, behavior: 'smooth' });
    }));
  }, [boardRef]);

  return { activeId, jumpTo };
};
